import { AxiosRequest } from "_services/api";
import {
  IData,
  IHttpError,
  IHttpResponse,
} from "_services/api/interface/HttpResponse";
import { usePaginationSortSearch } from "containers/Table/hooks/usePaginationSortSearch";
import { useCallback, useState } from "react";
import { HttpResponse } from "_services/api/HttpResponse";
import { HttpError } from "_services/api/HttpError";
import { IOptions } from "components/DropdownSelect";
import {
  IFornecedorPJ,
  IFornecedorPF,
  IDescricaoProcessoFinanceiro,
} from "../interfaces/IFornecedor";
import { IFiltro } from "../interfaces/IFiltro";

interface IProps {
  semFiltroStatus: boolean;
}

export function useFornecedores(props: IProps = { semFiltroStatus: false }) {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const api = AxiosRequest.makeApi();
  const { totalRows, setTotalRows } = usePaginationSortSearch();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<(IFornecedorPJ | IFornecedorPF)[]>([]);
  const [error, setError] = useState<IHttpResponse<IData | any>>();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [dadosFornecedorEscolhido, setDadosFornecedorEscolhido] = useState<
    IFornecedorPJ | IFornecedorPF | null
  >(null);
  const [filtro, setFiltro] = useState<IFiltro>({});
  const [optionsFornecedores, setOptionsFornecedores] = useState<IOptions[]>(
    []
  );
  const [
    optionsProcessoFinanceiroDescricao,
    setOptionsProcessoFinanceiroDescricao,
  ] = useState<IOptions[]>([]);

  const gerarQueryParams = (data: IFiltro) => {
    const cpfCnpj = data?.cpfCnpj ? `cpfCnpj=${data.cpfCnpj}` : null;
    const fornecedor = data?.fornecedor
      ? `fornecedor=${data.fornecedor}`
      : null;
    const status = data?.status ? `status=${data.status}` : null;
    const uf = data?.uf ? `uf=${data.uf}` : null;
    const cidade = data?.cidade
      ? `cidade=${encodeURIComponent(data.cidade)}`
      : null;
    const atividadesPrincipais = data?.atividadesPrincipais?.length
      ? `atividadesPrincipais=${encodeURIComponent(
          data.atividadesPrincipais.join("|")
        )}`
      : null;

    return [cpfCnpj, fornecedor, status, uf, cidade, atividadesPrincipais]
      .filter((item) => item !== null)
      .join("&");
  };

  const listar = useCallback(
    async (dados: IFiltro) => {
      setLoading(true);
      setFiltro(dados);
      try {
        const queryParams = gerarQueryParams(dados);
        const { status, data } = await msAw.get(
          `/fornecedores?pagina=${page}&limite=${limit}&${queryParams}`
        );

        setDados(data.itens);
        setTotalRows(data.meta.total);
        const response = new HttpResponse(false, status, data);
        setError(response);
        return response;
      } catch (error: IHttpError<IData, any> | any) {
        const response = new HttpError<IData>(true, error);
        setError(response);
        setDados([]);
        setTotalRows(0);
        return response;
      } finally {
        setLoading(false);
      }
    },
    [page, limit, filtro]
  );

  const listarTodos = async (
    dados: IFiltro
  ): Promise<(IFornecedorPJ | IFornecedorPF)[]> => {
    setLoading(true);
    try {
      const queryParams = gerarQueryParams(dados);
      const { data } = await msAw.get(
        `/fornecedores?pagina=1&limite=500000&${queryParams}`
      );

      return data.itens;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const gerarOptionsFornecedores = async () => {
    setLoading(true);
    try {
      const { data, status } = await msAw.get(
        `/fornecedores?limite=0${
          props.semFiltroStatus ? "&semFiltroStatus=SIM" : ""
        }`
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      setOptionsFornecedores(
        data.itens.map((fornecedor: IFornecedorPJ | IFornecedorPF) => {
          return { value: fornecedor.id, label: fornecedor.nomeRazaoSocial };
        })
      );
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      setOptionsFornecedores([]);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const buscar = async (id: number) => {
    setLoading(true);
    try {
      const { data, status } = await msAw.get(`/fornecedores/${id}`);

      const response = new HttpResponse(false, status, data);
      setError(response);
      setDadosFornecedorEscolhido(data);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      setDadosFornecedorEscolhido(null);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const atualizar = async (
    id: number,
    dados: IFornecedorPF | IFornecedorPJ
  ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.put(`/fornecedores/${id}`, dados);

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const inserir = async (dados: IFornecedorPF | IFornecedorPJ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.post(`/fornecedores`, dados);

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const atualizarDescricaoProcessoFinanceiroFornecedor = async (
    id: number,
    idDescricaoProcessoFinanceiroFornecedor: number,
    dados: IDescricaoProcessoFinanceiro
  ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.put(
        `/fornecedores/${id}/financeiro/${idDescricaoProcessoFinanceiroFornecedor}`,
        dados
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const inserirDescricaoProcessoFinanceiroFornecedor = async (
    id: number,
    dados: IDescricaoProcessoFinanceiro
  ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.post(
        `/fornecedores/${id}/financeiro`,
        dados
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const excluirDescricaoProcessoFinanceiroFornecedor = async (
    id: number,
    idDescricaoProcessoFinanceiroFornecedor: number
  ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.patch(
        `/fornecedores/${id}/financeiro/${idDescricaoProcessoFinanceiroFornecedor}`
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const ativacao = async (id: number, checked: boolean) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.put(`/fornecedores/${id}/ativacao`, {
        status: checked ? "ATIVO" : "INATIVO",
      });

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const listarProcessoFinanceiroDescricao = async () => {
    setLoading(true);
    try {
      const { status, data } = await api.get(`processos/financeiro/descricao`);

      const response = new HttpResponse(false, status, data);
      setError(response);
      setOptionsProcessoFinanceiroDescricao(
        data.map((item: any) => {
          return { label: item.descricao, value: item.id };
        })
      );
      return response;
    } catch (error) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      setOptionsProcessoFinanceiroDescricao([]);
      return response;
    } finally {
      setLoading(false);
    }
  };

  return {
    listar,
    listarTodos,
    error,
    totalRows,
    page,
    limit,
    loading,
    setLoading,
    dados,
    setDados,
    setPage,
    setLimit,
    setDadosFornecedorEscolhido,
    dadosFornecedorEscolhido,
    atualizar,
    ativacao,
    filtro,
    setFiltro,
    optionsFornecedores,
    gerarOptionsFornecedores,
    buscar,
    inserir,
    inserirDescricaoProcessoFinanceiroFornecedor,
    atualizarDescricaoProcessoFinanceiroFornecedor,
    excluirDescricaoProcessoFinanceiroFornecedor,
    listarProcessoFinanceiroDescricao,
    optionsProcessoFinanceiroDescricao,
  };
}
